import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Segment, FormField,Button,Input, Header, FormGroup, Loader, Message, Icon, MessageHeader, MessageContent} from "semantic-ui-react";
import { useQuery } from "react-query";
import { getConfig, setConfig } from "../queries";
import { useMutation } from "react-query";
import { toast } from "react-semantic-toasts";
import { useAppContext } from "../AppContext";
import { getDatetimeFromDay } from "../TimeStamp";
import { readFromToken } from "../auth.ts";


const ConfigEditPage = () => {
  const { t, i18n } = useTranslation();
  const { rtl } = useAppContext();
  
  const exampleReducer = (state, action) => {
    if (action.section === 'OPENING_HOURS') {
        return {
            ...state,
            working_hours: {
                ...state.working_hours,
                [action.day] : {
                    ...state.working_hours[action.day],
                    [action.type]: action.value,
                }
            }
        }
    }

    else if (action.section === 'INITIAL') {
        return action.value;
    }

    else if (action.section === 'FIELDS') {
        return {
            ...state,
            [action.field]: action.value
        }
    }

    return state;
  };

  const canChangeSettings = (time) =>{
    const d = new Date();
    const availableFrom = getDatetimeFromDay(time, 'Thursday');
    return d.getDay() > 4 || (d.getDay() === 4 && d.getHours() >= availableFrom.getHours() && d.getMinutes() > availableFrom.getMinutes())
  }
    


  const [config, dispatch] = React.useReducer(exampleReducer, {
      hasData: false,
      appointment_time: null,
      min_time_to_set: null,
      min_time_before_cancel: null,
      default_username: null,
      default_password: null,
      working_hours: Object.fromEntries(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'].map(day => [day, {
          start: null,
          break_start: null,
          break_finish: null,
          end: null,
      }])),
    });

    const {
        data: configQueryData,
        error, // TODO: error
        isLoading,
        refetch,
    } = useQuery("getConfigData", getConfig, {
        onSettled: (data) => {
            if (!data) return;

            dispatch({ section: 'INITIAL', value:  {
                hasData: true,
                appointment_time: data.appointment_time,
                min_time_to_set: data.min_time_to_set,
                min_time_before_cancel: data.min_time_before_cancel,
                default_username: data.default_username,
                default_password: data.default_password,
                working_hours: data.working_hours,
            } }); 
        }
    });

    const setConfigMutation = useMutation(setConfig, {
        onSettled: (response) => {refetch();onConfigSettled(response);}
    });

    const onSubmitConfig =(signout) => {
        const newConfig = {...config};
        delete newConfig.hasData;

        setConfigMutation.mutate({ config: newConfig });

        if (signout) {
            window.location.href = '/signout';
        }
    }

    const onConfigSettled = (response) => {
        switch(response.data){
            case "ok":
                toast({
                    type: 'success',
                    title: t('config-ok'),
                    time: 3500,
                    size: "small",
                });
                break;
            case "invalid-time-to-update":
                toast({
                    type: 'warning',
                    title: t('invalid-time-to-update'),
                    description: t("invalid-time-to-update-content"),
                    time: 3500,
                    size: "small",
                });
                break;
            case "update-failed":
                toast({
                    type: 'error',
                    title: t('update-failed'),
                    description: t("update-failed-content"),
                    time: 3500,
                    size: "small",
                });
                break
        }
    }

    return (configQueryData && config.hasData) ? (<>
        <Header size="large" textAlign="center">{t("edit-config")}</Header>
        <Form loading={getConfig.isLoading}>
            <Segment>
                <Header size="medium">{t("deafult-user-settings")}</Header>
                {
                    ['default_username', 'default_password']
                        .map(field => (
                            <FormField key={field} >
                                <label>{t(field)} </label>
                                <Input defaultValue={config[field]} onChange ={(_, { value }) => dispatch({ section: 'FIELDS', field, value }) } />
                            </FormField>
                        ))
                }            
            </Segment>
             { readFromToken('email') === config['default_username'] && (
                 <Message info size="small">{t('will-log-out')}</Message>
             )}
            <Button primary onClick={() => onSubmitConfig(readFromToken('email') === config['default_username'])} size="large" fluid>
                {readFromToken('email') === config['default_username'] ?  t("update-and-signout") : t("update-settings")}
            </Button>
            <br />
            {
            !canChangeSettings(configQueryData.working_hours['Thursday'].end) && (
                <Message info icon size="large">
                    <Icon style={{ marginLeft: rtl ? 20 : 0 }} name='settings'/>
                    <MessageContent>
                        <MessageHeader>{t("can't-change-settings-on-weekday")}</MessageHeader>
                        <p>{`${t("can-change-settings-on-weekend-p1")} ${configQueryData.working_hours['Thursday'].end} ${t("can-change-settings-on-weekend-p2")}`}</p>
                    </MessageContent>
                </Message>
            )
            }
            <Segment>
                <Header size="medium">{t("app-settings")}</Header>
                {
                    ['appointment_time', 'min_time_to_set', 'min_time_before_cancel']
                        .map(field => (
                            <FormField key={field} >
                                <label>{t(field)} </label>
                                <Input defaultValue={config[field]} onChange ={(_, { value }) => dispatch({ section: 'FIELDS', field, value }) } readOnly={!canChangeSettings(configQueryData.working_hours['Thursday'].end)} />
                            </FormField>
                        ))
                }            
            </Segment>
            <Segment>
                <Header size="medium">{t("opening-hours")}</Header>
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'].map((day, index) => (
                    <React.Fragment key={`form-${day}`}>
                        <Header size="small">{t(day)}</Header>
                        <FormGroup widths='equal'>
                            {
                                ['start', 'break_start', 'break_end', 'end'].map((type) => {
                                    return (
                                        <FormField key={`time-${day}-${type}`}>
                                            <label>{t(type)}</label>
                                            <Form.Input key={`${day}-${type}-input`} type="time" defaultValue={config.working_hours[day][type]} onChange={(_, { value }) => dispatch({ section: 'OPENING_HOURS', day, type, value })} readOnly={!canChangeSettings(configQueryData.working_hours['Thursday'].end)} />
                                        </FormField>
                                )})
                            }
                        </FormGroup>                    
                    </React.Fragment>
                ))}
            </Segment>
            <Button primary onClick={() => onSubmitConfig(false)} size="large" fluid disabled={!canChangeSettings(configQueryData.working_hours['Thursday'].end)}>{t("update-settings")}</Button>
        </Form>
  </>) : (
    <Loader />
  );
};

export default ConfigEditPage;
